import { useState, useMemo, useEffect } from "react";
import { useBusinessNoticeRecipientList } from "~/services/business/notify";
import type { BusinessNotifySnapshot } from "&/businessNotifyList";

const useNotifyReceive = (initial?: BusinessNotifySnapshot) => {
  const [filterType, setFilterType] = useState<string>(
    initial?.filterType ?? "전체"
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(
    initial?.selectedType ?? "최신순"
  );
  const [searchText, setSearchText] = useState(initial?.searchText ?? "");
  const [searchKeyword, setSearchKeyword] = useState<string | null>(
    initial?.searchKeyword ?? null
  ); // 실제 검색에 사용될 키워드
  const [page, setPage] = useState(initial?.page ?? 1);
  // 복원 부트스트랩: 저장된 페이지 수만큼(page*10)을 1페이지 한 번에 재조회 후 교체.
  // 다음 페이지를 붙이거나 검색/필터가 바뀌는 순간 일반 페이징(size 10)으로 전환
  const [bootstrap, setBootstrap] = useState(!!initial);
  const [accumulatedContent, setAccumulatedContent] = useState<any[]>([]);

  // API 요청 파라미터
  const requestParams = useMemo(() => {
    let readYn = null;
    let replyYn = null;

    // filterType에 따라 readYn과 replyYn 설정
    switch (filterType) {
      case "전체":
        readYn = null;
        replyYn = null;
        break;
      case "안읽음":
        readYn = "N";
        replyYn = null;
        break;
      case "댓글없음":
        readYn = null;
        replyYn = "N";
        break;
      default:
        readYn = null;
        replyYn = null;
    }

    return {
      searchKeyword: searchKeyword, // 검색 버튼 클릭 시에만 업데이트되는 키워드 사용
      cdRecipientType: "RECIPIENT_001",
      noticeType: "NOTI_TYPE_003",
      page: bootstrap ? 1 : page,
      size: bootstrap ? page * 10 : 10,
      readYn: readYn,
      sortOrder: selectedType === "최신순" ? "DESC" : "ASC",
      replyYn: replyYn,
    };
  }, [page, selectedType, filterType, searchKeyword, bootstrap]);

  // API 호출
  const {
    data: apiData,
    isLoading,
    error,
    refetch,
  } = useBusinessNoticeRecipientList(requestParams);

  // 페이지별 데이터 누적 (스크롤 시 추가 로드)
  useEffect(() => {
    if (!apiData?.content) return;
    const currentPage = requestParams.page;
    setAccumulatedContent((prev) =>
      currentPage === 1 ? apiData.content : [...prev, ...apiData.content]
    );
  }, [apiData, requestParams.page]);

  const receivedData = useMemo(() => {
    if (apiData && Array.isArray(apiData)) {
      return apiData;
    }
    return accumulatedContent;
  }, [apiData, accumulatedContent]);

  const handleSearch = (value: string) => {
    setBootstrap(false);
    setSearchKeyword(value || null); // 검색 버튼 클릭 시에만 실제 검색 키워드 업데이트
    setPage(1); // 검색 시 첫 페이지로 리셋
    // searchKeyword가 변경되면 requestParams가 변경되고,
    // useBusinessNoticeRecipientList가 자동으로 재호출됩니다.
  };

  const onLoad = () => {
    // 부트스트랩(일괄 조회) 이후 다음 페이지부터는 일반 페이징으로 전환
    setBootstrap(false);
    setPage((prev) => prev + 1); // 다음 페이지 로드
  };

  const hasMore = apiData ? !apiData.last : false;

  return {
    apiData,
    receivedData,
    hasMore,
    onLoad,
    handleSearch,
    filterType,
    setFilterType: (type: string) => {
      setBootstrap(false);
      setFilterType(type);
      setPage(1); // 필터 변경 시 첫 페이지로 리셋
      setSearchKeyword(null); // 필터 변경 시 검색 키워드 초기화
    },
    dropdownOpen,
    setDropdownOpen,
    selectedType,
    setSelectedType: (type: string) => {
      setBootstrap(false);
      setSelectedType(type);
      setPage(1); // 정렬 변경 시 첫 페이지로 리셋
      setSearchKeyword(null); // 정렬 변경 시 검색 키워드 초기화
    },
    isLoading,
    error,
    refetch,
    page,
    searchKeyword,
    searchText,
    setSearchText,
  };
};

export default useNotifyReceive;

// const dummy = [
//   {
//     key: "1",
//     kind: "notice",
//     title: "또치 알림장이 도착했어요.",
//     type: "calendar",
//     petName: "또치",
//     petType: "강아지",
//     petImg: pet1Src,
//     petWeight: "20kg",
//     date: "2024.05.05 13:00",
//     read: true,
//     img: [],
//     text: "안녕하세요. 오늘 우리 또치는 사료도 잘먹고 어질리티 훈련을 잘받았어요. 간식으로 오후에 닭발을 먹었어요 오후에 운동장에서 뛰어놀고 다른 강아지와 함께 물놀이에도 참여했어요. 낮잠은 실내 캔넬에서 1시간가량 잤어요.",
//   },
//   {
//     key: "2",
//     kind: "notice",
//     title: "또치 알림장이 도착했어요.",
//     type: "calendar",
//     petName: "또치",
//     petType: "강아지",
//     petWeight: "10kg",
//     date: "2024.05.05 13:00",
//     petImg: pet1Src,
//     read: false,
//     img: [],
//     text: "안녕하세요.\n오늘 우리 또치는 사료도 잘먹고 어질리티 훈련을 잘받았어요. 간식으로 오후에 닭발을 먹었어요 오후에 운동장에서 뛰어놀고 다른 강아지와 함께 물놀이에도 참여했어요. 낮잠은 실내 캔넬에서 1시간가량 잤어요.",
//   },
//   {
//     key: "3",
//     kind: "notice",
//     title: "또치 알림장이 도착했어요.",
//     type: "notify",
//     petName: "또치",
//     petType: "강아지",
//     petWeight: "30kg",
//     date: "2024.05.05 13:00",
//     petImg: pet1Src,
//     read: true,
//     img: [pet1Src, pet1Src],
//     text: "안녕하세요.\n오늘 우리 또치는 사료도 잘먹고 어질리티 훈련을 잘받았어요. 간식으로 오후에 닭발을 먹었어요 오후에 운동장에서 뛰어놀고 다른 강아지와 함께 물놀이에도 참여했어요. 낮잠은 실내 캔넬에서 1시간가량 잤어요.",
//   },
// ];
